import {UseMutationResult, UseQueryResult, useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import axios, {AxiosError} from 'axios';
import {toast} from 'react-toastify';
import {useTranslation} from 'react-i18next';
import {QUERY_KEYS} from '../../../../utils/queryKeys';
import {type TypedTranslation} from '../../../../i18n';
import {
    createPresentationFolder,
    deletePresentationFolder,
    fetchPresentationFolders,
    movePresentationsToFolder,
    renamePresentationFolder,
} from './presentationFolder.service';
import {IPresentationFolder} from './types';

interface FolderErrorResponse {
    message?: string;
    roomNames?: string[];
}

function translateFolderError(message: unknown, fallbackKey: TypedTranslation, t: (key: TypedTranslation) => string): string {
    if (typeof message === 'string' && message.startsWith('apiResponses.')) {
        return t(message as TypedTranslation);
    }
    return t(fallbackKey);
}

export function usePresentationFoldersQuery(enabled = true): UseQueryResult<IPresentationFolder[]> {
    return useQuery(QUERY_KEYS.presentationFolder.list(), () => fetchPresentationFolders(), {
        refetchOnWindowFocus: false,
        staleTime: 60 * 1000,
        enabled,
    });
}

export function useCreatePresentationFolder(
    onSuccess?: (folderId: string) => void
): UseMutationResult<IPresentationFolder, unknown, string> {
    const client = useQueryClient();
    const {t} = useTranslation();
    return useMutation((name: string) => createPresentationFolder(name), {
        onSuccess: (folder) => {
            client.invalidateQueries({queryKey: QUERY_KEYS.presentationFolder.lists()});
            toast.success(t('editorPage.folders.folderCreated'));
            onSuccess?.(folder._id);
        },
        onError: (error) => {
            const message = axios.isAxiosError(error) ? (error as AxiosError<FolderErrorResponse>).response?.data?.message : undefined;
            toast.error(translateFolderError(message, 'editorPage.folders.folderCreateFailed', t));
        },
    });
}

export function useRenamePresentationFolder(): UseMutationResult<IPresentationFolder, unknown, {folderId: string; name: string}> {
    const client = useQueryClient();
    const {t} = useTranslation();
    return useMutation((variables: {folderId: string; name: string}) => renamePresentationFolder(variables.folderId, variables.name), {
        onSuccess: () => {
            client.invalidateQueries({queryKey: QUERY_KEYS.presentationFolder.lists()});
            toast.success(t('editorPage.folders.folderRenamed'));
        },
        onError: (error) => {
            const message = axios.isAxiosError(error) ? (error as AxiosError<FolderErrorResponse>).response?.data?.message : undefined;
            toast.error(translateFolderError(message, 'editorPage.folders.folderRenameFailed', t));
        },
    });
}

export function useDeletePresentationFolder(): UseMutationResult<void, unknown, string> {
    const client = useQueryClient();
    const {t} = useTranslation();
    return useMutation((folderId: string) => deletePresentationFolder(folderId), {
        onSuccess: () => {
            client.invalidateQueries({queryKey: QUERY_KEYS.presentationFolder.lists()});
            client.invalidateQueries({queryKey: QUERY_KEYS.presentation.lists()});
            toast.success(t('editorPage.folders.folderDeleted'));
        },
        onError: (error) => {
            const data = axios.isAxiosError(error) ? (error as AxiosError<FolderErrorResponse>).response?.data : undefined;
            if (data?.message === 'apiResponses.presentationInRoom' && data?.roomNames) {
                toast.error(`${t(data.message as TypedTranslation)} ${data.roomNames}`);
            } else {
                toast.error(t('editorPage.folders.folderDeleteFailed'));
            }
        },
    });
}

export function useMovePresentationsToFolder(): UseMutationResult<boolean, unknown, {presentationIds: string[]; folderId: string | null}> {
    const client = useQueryClient();
    const {t} = useTranslation();
    return useMutation(
        (variables: {presentationIds: string[]; folderId: string | null}) =>
            movePresentationsToFolder(variables.presentationIds, variables.folderId),
        {
            onSuccess: () => {
                client.invalidateQueries({queryKey: QUERY_KEYS.presentation.lists()});
                client.invalidateQueries({queryKey: QUERY_KEYS.presentationFolder.lists()});
                toast.success(t('editorPage.folders.presentationsMoved'));
            },
            onError: () => {
                toast.error(t('editorPage.folders.moveFailed'));
            },
        }
    );
}
